<template>
  <div class="flex flex-column border-1">
    <div class="flex text-2xl justify-content-center">CALC FLOW TESTER</div>
    <div class="flex flex-column gap-4">
      <div class="flex flex-column gap-2">
        <div class="flex justify-content-center">
          VIA REFACTORED SCENARIO CALCS USING DESTRUCTURED SCENARIO
        </div>
        <!-- Controls Section -->
        <div class="flex justify-content-around p-4">
          <div class="flex gap-4">
            <div class="flex flex-column gap-2">
              <label>Max Depth</label>
              <InputNumber
                v-model="maxDepth"
                :min="1"
                :max="20"
                :step="1"
                class="w-24"
              />
            </div>
            <div class="flex flex-column gap-2">
              <label>Tolerance (PSI)</label>
              <InputNumber
                v-model="tolerance"
                mode="decimal"
                :minFractionDigits="0"
                :maxFractionDigits="3"
                :min="0.001"
                :max="10"
                class="w-24"
              />
            </div>
            <div class="flex flex-column gap-2">
              <label>Pump Hz</label>
              <ConverterInput
                label=""
                :model-value="parameters.pump_hz"
                :base-unit="FrequencyUnits.Hertz"
                :model="FrequencyUnits.options()"
                mode="write"
                :decimal-places="6"
                :rule-names="[
                  'vsd_freq_low',
                  'vsd_freq_high',
                  'switchboard_freq'
                ]"
                @update:model-value="
                  parametersForm.update({
                    pump_hz: $event
                  })
                "
              />
            </div>
            <div class="flex flex-column gap-2">
              <pre>{{ importantData }}</pre>
            </div>
          </div>
        </div>
        <div class="flex justify-content-around">
          <div class="flex gap-4 align-items-center">
            <Button @click="calcNewFlow">CALC NEW FLOW</Button>
            <div>Result: {{ theNewFlow }}</div>
            <div>{{ theNewStatus }}</div>
          </div>
        </div>
      </div>
    </div>

    <TabView>
      <TabPanel
        header="Result Set - Max Flow @ Intake"
        :disabled="flowResultRowsIntake?.length === 0"
      >
        <CalcFlowResultTable
          :flowResultRows="flowResultRowsIntake"
        ></CalcFlowResultTable>
      </TabPanel>
    </TabView>
  </div>
</template>

<script lang="ts" setup>
import {
  parametersFormKey,
  scenarioCalculationsKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict } from "@/utils/utils";
import { computed, ref } from "vue";
import { getPredictedDesiredFlowRateSTBD } from "../../../libs/calculations/predictiveCalcs/getPredictedDesiredFlowRateSTBD";
import type { HypotheticalFlowDataRow } from "@/libs/calculations/predictiveCalcs/getPredictedDesiredFlowRateSTBD/d";
import { MAX_DELTA_REQUIRED_PRESSURE } from "../../../../src/libs/calculations/constants";
import { FrequencyUnits } from "@/libs/units/frequency";
import ConverterInput from "@/components/ConverterInput.vue";
import CalcFlowResultTable from "./CalcFlowResultTable.vue";

const parametersForm = injectStrict(parametersFormKey);
const parameters = computed(() => parametersForm.data.value);

const _calcs = injectStrict(scenarioCalculationsKey);

// Control refs
const maxDepth = ref(8); // Default from doOneRow.ts
const tolerance = ref(MAX_DELTA_REQUIRED_PRESSURE);
const pumpFrequencyHz = ref(_calcs.pumpHz);

// const currentFlowRate =
//   _calcs.destructuredScenario.parameters?.value
//     .target_liquid_rate_std_barrel_day;

// const frequencyHz = ref({
//   pumpHz: _calcs.pumpHz,
//   produced:
//     _calcs.pumpCalculations.aggregate.producedDischargePressurePSIA.value,
//   required:
//     _calcs.pumpCalculations.aggregate.requiredDischargePressurePSIA.value,
//   target_liquid_rate_std_barrel_day: currentFlowRate
// });

const theNewFlow = ref(0);
const theNewStatus = ref();
const flowResultRows = ref();
const flowResultRowsIntake = ref<HypotheticalFlowDataRow[] | undefined>([]);

const calcNewFlow = () => {
  // Update pump frequency in calculations
  if (_calcs.pumpHz.value !== pumpFrequencyHz.value) {
    // _calcs.pumpHz.value = pumpFrequencyHz.value;
  }

  const theData = getPredictedDesiredFlowRateSTBD(
    _calcs,
    parameters.value,
    tolerance.value,
    maxDepth.value
  );

  if (theData) {
    theNewFlow.value = theData.targetRow?.hypotheticalFlowRateSTBD || -1;
    theNewStatus.value = theData.status;
    flowResultRows.value = theData.rows;
    flowResultRowsIntake.value =
      theData.allResultSets?.resultSetRowsMaxFlowAtIntake;
  }
};

const importantData = computed(() => {
  const {
    pumpHz,
    maxFlowRateBBLD,
    primaryPump,
    maxFlowRateAtIntakeBBLD,
    desiredLiquidRateSTDB,
    pumpCalculations: {
      individual: individualPumpCalculations,
      aggregate: aggregatePumpCalculations
    }
  } = _calcs;

  const graphMax =
    individualPumpCalculations.value[primaryPump.value?.id].graphMax;

  const producedDischargePressurePSIA =
    aggregatePumpCalculations.producedDischargePressurePSIA.value;
  const requiredDischargePressurePSIA =
    aggregatePumpCalculations.requiredDischargePressurePSIA.value;
  return {
    pumpHz,
    desiredLiquidRateSTDB,
    maxFlowRateBBLD,
    maxFlowRateAtIntakeBBLD,
    producedDischargePressurePSIA,
    requiredDischargePressurePSIA,
    graphMax
  };
});
</script>
